import React, { useState, useEffect } from 'react';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  CurrencyDollarIcon,
  UserGroupIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  EyeSlashIcon,
  LinkIcon,
  TagIcon,
} from '@heroicons/react/24/outline';
import { PlusIcon as PlusSolidIcon } from '@heroicons/react/20/solid';
import Button from '../../../components/ui/Button';
import { Card, CardContent } from '../../../components/ui/Card';
import Modal, { ModalContent, ModalFooter } from '../../../components/ui/Modal';
import FormInput from '../../../components/ui/FormInput';
import Textarea from '../../../components/ui/Textarea';
import { creatorService } from '../../../services/creatorService';

interface Tier {
  id: string;
  name: string;
  description: string;
  amount: number;
  currency: string;
  billingCycle: 'MONTHLY' | 'YEARLY';
  benefits: string[];
  isPublic: boolean;
  maxPatrons?: number;
  currentPatrons: number;
  customButtonText?: string;
  accessUrl?: string;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  freeTrialDays?: number;
}

interface TierFormData {
  name: string;
  description: string;
  amount: string;
  currency: string;
  billingCycle: 'MONTHLY' | 'YEARLY';
  benefits: string[];
  isPublic: boolean;
  maxPatrons?: string;
  customButtonText?: string;
  accessUrl?: string;
  freeTrialDays?: string;
  isPerCreation?: boolean;
  isScheduledVisibility?: boolean;
  visibilityStartDate?: string;
  visibilityEndDate?: string;
}

const TiersPage: React.FC = () => {
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTier, setEditingTier] = useState<Tier | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<TierFormData>({
    name: '',
    description: '',
    amount: '',
    currency: 'USD',
    billingCycle: 'MONTHLY',
    benefits: [''],
    isPublic: true,
    maxPatrons: '',
    customButtonText: '',
    accessUrl: '',
    freeTrialDays: '',
    isPerCreation: false,
    isScheduledVisibility: false,
    visibilityStartDate: '',
    visibilityEndDate: '',
  });
  const [newBenefit, setNewBenefit] = useState('');

  useEffect(() => {
    loadTiers();
  }, []);

  const loadTiers = async () => {
    setLoading(true);
    try {
      const apiTiers = await creatorService.getMyTiers();
      // Map API shape if necessary
      setTiers(apiTiers as any);
    } catch (error) {
      console.error('Failed to load tiers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (tier?: Tier) => {
    if (tier) {
      setEditingTier(tier);
      setFormData({
        name: tier.name,
        description: tier.description,
        amount: tier.amount.toString(),
        currency: tier.currency,
        billingCycle: tier.billingCycle,
        benefits: [...tier.benefits],
        isPublic: tier.isPublic,
        maxPatrons: tier.maxPatrons?.toString() || '',
        customButtonText: tier.customButtonText || '',
        accessUrl: tier.accessUrl || '',
        freeTrialDays: tier.freeTrialDays?.toString() || '',
        isPerCreation: (tier as any).isPerCreation || false,
        isScheduledVisibility: (tier as any).isScheduledVisibility || false,
        visibilityStartDate: (tier as any).visibilityStartDate ? new Date((tier as any).visibilityStartDate).toISOString().slice(0, 16) : '',
        visibilityEndDate: (tier as any).visibilityEndDate ? new Date((tier as any).visibilityEndDate).toISOString().slice(0, 16) : '',
      });
    } else {
      setEditingTier(null);
      setFormData({
        name: '',
        description: '',
        amount: '',
        currency: 'USD',
        billingCycle: 'MONTHLY',
        benefits: [''],
        isPublic: true,
        maxPatrons: '',
        customButtonText: '',
        accessUrl: '',
        freeTrialDays: '',
        isPerCreation: false,
        isScheduledVisibility: false,
        visibilityStartDate: '',
        visibilityEndDate: '',
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingTier(null);
    setFormData({
      name: '',
      description: '',
      amount: '',
      currency: 'USD',
      billingCycle: 'MONTHLY',
      benefits: [''],
      isPublic: true,
      maxPatrons: '',
      customButtonText: '',
      accessUrl: '',
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const payload: any = {
        name: formData.name,
        description: formData.description,
        amount: parseFloat(formData.amount),
        currency: formData.currency,
        billingCycle: formData.billingCycle,
        benefits: formData.benefits.filter(Boolean),
        isPublic: formData.isPublic,
        maxPatrons: formData.maxPatrons ? parseInt(formData.maxPatrons) : undefined,
        customButtonText: formData.customButtonText || undefined,
        accessUrl: formData.accessUrl || undefined,
        freeTrialDays: formData.freeTrialDays ? parseInt(formData.freeTrialDays) : undefined,
        isPerCreation: formData.isPerCreation,
        isScheduledVisibility: formData.isScheduledVisibility,
        visibilityStartDate: formData.visibilityStartDate || undefined,
        visibilityEndDate: formData.visibilityEndDate || undefined,
      };

      if (editingTier) {
        const updated = await creatorService.updateTier(editingTier.id, payload);
        setTiers(prev => prev.map(tier => (tier.id === editingTier.id ? updated : tier)));
      } else {
        const created = await creatorService.createTier(payload);
        setTiers(prev => [...prev, created]);
      }

      handleCloseModal();
    } catch (error) {
      console.error('Failed to save tier:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteTier = async (tierId: string) => {
    if (!confirm('Are you sure you want to delete this tier? This cannot be undone.')) {
      return;
    }

    try {
      await creatorService.deleteTier(tierId);
      setTiers(prev => prev.filter(tier => tier.id !== tierId));
    } catch (error) {
      console.error('Failed to delete tier:', error);
    }
  };

  const handleToggleTierStatus = async (tierId: string) => {
    try {
      setTiers(prev =>
        prev.map(tier =>
          tier.id === tierId
            ? { ...tier, isActive: !tier.isActive, updatedAt: new Date().toISOString() }
            : tier
        )
      );
    } catch (error) {
      console.error('Failed to toggle tier status:', error);
    }
  };

  const handleToggleVisibility = async (tierId: string) => {
    try {
      setTiers(prev =>
        prev.map(tier =>
          tier.id === tierId
            ? { ...tier, isPublic: !tier.isPublic, updatedAt: new Date().toISOString() }
            : tier
        )
      );
    } catch (error) {
      console.error('Failed to toggle tier visibility:', error);
    }
  };

  const handleReorder = async (tierId: string, direction: 'up' | 'down') => {
    try {
      setTiers(prev => {
        const index = prev.findIndex(tier => tier.id === tierId);
        if (index === -1) return prev;

        const newTiers = [...prev];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;

        if (targetIndex >= 0 && targetIndex < newTiers.length) {
          // Swap tiers
          [newTiers[index], newTiers[targetIndex]] = [newTiers[targetIndex], newTiers[index]];

          // Update sortOrder
          return newTiers.map((tier, i) => ({
            ...tier,
            sortOrder: i + 1,
          }));
        }

        return prev;
      });
    } catch (error) {
      console.error('Failed to reorder tiers:', error);
    }
  };

  const addBenefit = () => {
    if (newBenefit.trim()) {
      setFormData(prev => ({
        ...prev,
        benefits: [...prev.benefits, newBenefit.trim()]
      }));
      setNewBenefit('');
    }
  };

  const removeBenefit = (index: number) => {
    setFormData(prev => ({
      ...prev,
      benefits: prev.benefits.filter((_, i) => i !== index)
    }));
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount);
  };

  const monthlyRevenue = tiers.reduce((total, tier) => total + (tier.currentPatrons * tier.amount), 0);
  const totalPatrons = tiers.reduce((total, tier) => total + tier.currentPatrons, 0);

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-48 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Tier Management</h1>
          <p className="text-gray-600 mt-2">
            Create and manage your membership tiers
          </p>
        </div>
        <Button
          leftIcon={<PlusIcon className="w-5 h-5" />}
          onClick={() => handleOpenModal()}
        >
          Create Tier
        </Button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 rounded-lg">
                <UserGroupIcon className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">{totalPatrons}</p>
                <p className="text-sm text-gray-600">Total Patrons</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 rounded-lg">
                <CurrencyDollarIcon className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(monthlyRevenue, 'USD')}</p>
                <p className="text-sm text-gray-600">Monthly Revenue</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center">
              <div className="p-3 bg-purple-100 rounded-lg">
                <TagIcon className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">{tiers.length}</p>
                <p className="text-sm text-gray-600">Active Tiers</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center">
              <div className="p-3 bg-orange-100 rounded-lg">
                <CurrencyDollarIcon className="w-6 h-6" style={{ color: 'var(--patreon-orange)' }} />
              </div>
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">
                  {totalPatrons > 0 ? formatCurrency(monthlyRevenue / totalPatrons, 'USD') : '$0'}
                </p>
                <p className="text-sm text-gray-600">Avg. Pledge</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tiers List */}
      <div className="space-y-6">
        {tiers.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <TagIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No tiers created</h3>
              <p className="text-gray-600 mb-4">
                Create your first membership tier to start earning from your patrons.
              </p>
              <Button
                leftIcon={<PlusIcon className="w-5 h-5" />}
                onClick={() => handleOpenModal()}
              >
                Create Your First Tier
              </Button>
            </CardContent>
          </Card>
        ) : (
          tiers.map((tier) => (
            <Card key={tier.id} className={`${!tier.isActive ? 'opacity-75' : ''}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-xl font-semibold text-gray-900">{tier.name}</h3>
                      {tier.isPublic ? (
                        <EyeIcon className="w-5 h-5 text-green-500" title="Public" />
                      ) : (
                        <EyeSlashIcon className="w-5 h-5 text-gray-400" title="Hidden" />
                      )}
                      {tier.isActive ? (
                        <CheckCircleIcon className="w-5 h-5 text-green-500" title="Active" />
                      ) : (
                        <XCircleIcon className="w-5 h-5 text-gray-400" title="Inactive" />
                      )}
                    </div>

                    <p className="text-gray-600 mb-4">{tier.description}</p>

                    <div className="flex items-center space-x-6 text-sm text-gray-500">
                      <span className="flex items-center">
                        <CurrencyDollarIcon className="w-4 h-4 mr-1" />
                        {formatCurrency(tier.amount, tier.currency)}/{tier.billingCycle === 'MONTHLY' ? 'month' : 'year'}
                      </span>
                      <span className="flex items-center">
                        <UserGroupIcon className="w-4 h-4 mr-1" />
                        {tier.currentPatrons} patrons
                      </span>
                      {tier.maxPatrons && (
                        <span className="flex items-center">
                          <PlusSolidIcon className="w-4 h-4 mr-1" />
                          Limited to {tier.maxPatrons}
                        </span>
                      )}
                      {tier.freeTrialDays && tier.freeTrialDays > 0 && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                          🎁 {tier.freeTrialDays}-day trial
                        </span>
                      )}
                      <span className="flex items-center">
                        {formatCurrency(tier.currentPatrons * tier.amount, tier.currency)}/month
                      </span>
                    </div>

                    {tier.benefits.length > 0 && (
                      <div className="mt-4">
                        <h4 className="font-medium text-gray-900 mb-2">Benefits:</h4>
                        <ul className="space-y-1">
                          {tier.benefits.map((benefit, index) => (
                            <li key={index} className="flex items-center text-sm text-gray-600">
                              <CheckCircleIcon className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                              {benefit}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col space-y-2 ml-6">
                    <div className="flex space-x-1">
                      <button
                        onClick={() => handleReorder(tier.id, 'up')}
                        disabled={tier.sortOrder === 1}
                        className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ArrowUpIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleReorder(tier.id, 'down')}
                        disabled={tier.sortOrder === tiers.length}
                        className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ArrowDownIcon className="w-4 h-4" />
                      </button>
                    </div>

                    <button
                      onClick={() => handleOpenModal(tier)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                    >
                      <PencilIcon className="w-4 h-4" />
                    </button>

                    <button
                      onClick={() => handleToggleVisibility(tier.id)}
                      className="p-2 text-yellow-600 hover:bg-yellow-50 rounded-lg"
                    >
                      {tier.isPublic ? (
                        <EyeSlashIcon className="w-4 h-4" />
                      ) : (
                        <EyeIcon className="w-4 h-4" />
                      )}
                    </button>

                    <button
                      onClick={() => handleToggleTierStatus(tier.id)}
                      className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg"
                    >
                      {tier.isActive ? (
                        <XCircleIcon className="w-4 h-4" />
                      ) : (
                        <CheckCircleIcon className="w-4 h-4" />
                      )}
                    </button>

                    <button
                      onClick={() => handleDeleteTier(tier.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create/Edit Tier Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingTier ? 'Edit Tier' : 'Create New Tier'}
        size="2xl"
      >
        <form onSubmit={handleSubmit}>
          <ModalContent>
            <div className="space-y-6">
              <FormInput
                label="Tier Name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Bronze Supporter"
                required
              />

                <Textarea
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe what patrons get with this tier"
                required
                rows={3}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormInput
                  label="Amount"
                  type="number"
                  step="0.01"
                  min="1"
                  value={formData.amount}
                  onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                  placeholder="5.00"
                  required
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Billing Cycle
                  </label>
                  <select
                    value={formData.billingCycle}
                    onChange={(e) => setFormData(prev => ({ ...prev, billingCycle: e.target.value as 'MONTHLY' | 'YEARLY' }))}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-2 focus:ring-offset-2"
                    style={{ '--tw-ring-color': 'var(--patreon-orange)' } as React.CSSProperties}
                    onFocus={(e) => { e.target.style.borderColor = 'var(--patreon-orange)'; e.target.style.boxShadow = '0 0 0 3px rgba(255, 107, 53, 0.1)'; }}
                    onBlur={(e) => { e.target.style.borderColor = ''; e.target.style.boxShadow = ''; }}
                    disabled={formData.isPerCreation}
                  >
                    <option value="MONTHLY">Monthly</option>
                    <option value="YEARLY">Yearly</option>
                  </select>
                </div>
              </div>

              {/* Per-Creation Billing Toggle */}
              <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg">
                <input
                  type="checkbox"
                  id="isPerCreation"
                  checked={formData.isPerCreation || false}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    isPerCreation: e.target.checked,
                    billingCycle: e.target.checked ? 'MONTHLY' : prev.billingCycle
                  }))}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-patreon-orange focus:ring-patreon-orange"
                />
                <label htmlFor="isPerCreation" className="flex-1 cursor-pointer">
                  <span className="block text-sm font-medium text-gray-900">
                    Per-Creation Billing
                  </span>
                  <span className="block text-sm text-gray-600 mt-1">
                    Charge patrons each time you publish new content instead of monthly. 
                    Ideal for creators who post irregularly.
                  </span>
                </label>
              </div>

              <FormInput
                label="Maximum Patrons (Optional)"
                type="number"
                min="1"
                value={formData.maxPatrons}
                onChange={(e) => setFormData(prev => ({ ...prev, maxPatrons: e.target.value }))}
                placeholder="Leave empty for unlimited"
                helperText="Create an exclusive tier with limited spots"
              />

              <FormInput
                label="Free Trial Days (Optional)"
                type="number"
                min="1"
                max="365"
                value={formData.freeTrialDays}
                onChange={(e) => setFormData(prev => ({ ...prev, freeTrialDays: e.target.value }))}
                placeholder="0"
                helperText="Number of free trial days (e.g., 7 for a week trial)"
              />

              <FormInput
                label="Custom Button Text (Optional)"
                value={formData.customButtonText}
                onChange={(e) => setFormData(prev => ({ ...prev, customButtonText: e.target.value }))}
                placeholder="Become a Patron"
                helperText="Custom text for the support button"
              />

              <FormInput
                label="Access URL (Optional)"
                value={formData.accessUrl}
                onChange={(e) => setFormData(prev => ({ ...prev, accessUrl: e.target.value }))}
                placeholder="https://discord.gg/yourserver"
                helperText="Link to exclusive community or content"
                startIcon={<LinkIcon className="w-4 h-4 text-gray-400" />}
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tier Benefits
                </label>
                <div className="space-y-2">
                  {formData.benefits.map((benefit, index) => (
                    <div key={index} className="flex space-x-2">
                      <FormInput
                        value={benefit}
                        onChange={(e) => {
                          const newBenefits = [...formData.benefits];
                          newBenefits[index] = e.target.value;
                          setFormData(prev => ({ ...prev, benefits: newBenefits }));
                        }}
                        placeholder="Enter a benefit"
                        containerClassName="flex-1"
                      />
                      <button
                        type="button"
                        onClick={() => removeBenefit(index)}
                        className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ))}

                  <div className="flex space-x-2">
                    <FormInput
                      value={newBenefit}
                      onChange={(e) => setNewBenefit(e.target.value)}
                      placeholder="Add a new benefit"
                      containerClassName="flex-1"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addBenefit();
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={addBenefit}
                      className="px-4 py-2 text-white rounded-lg patreon-primary-gradient"
                      style={{ background: 'var(--patreon-btn-primary-bg)', boxShadow: 'var(--patreon-btn-primary-shadow)' }}
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div>

              {/* Advanced Visibility Scheduling */}
              <div className="space-y-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isScheduledVisibility"
                    checked={formData.isScheduledVisibility}
                    onChange={(e) => setFormData(prev => ({ ...prev, isScheduledVisibility: e.target.checked }))}
                    className="h-4 w-4 border-gray-300 rounded"
                    style={{ color: 'var(--patreon-orange)' } as React.CSSProperties}
                    onFocus={(e) => { e.target.style.boxShadow = '0 0 0 3px rgba(255, 107, 53, 0.1)'; }}
                    onBlur={(e) => { e.target.style.boxShadow = ''; }}
                  />
                  <label htmlFor="isScheduledVisibility" className="ml-2 block text-sm font-medium text-gray-900">
                    Advanced Visibility Scheduling
                  </label>
                </div>

                {formData.isScheduledVisibility && (
                  <div className="ml-6 space-y-3 p-4 bg-gray-50 rounded-lg">
                    <div>
                      <label htmlFor="visibilityStartDate" className="block text-sm font-medium text-gray-700 mb-1">
                        Start Date (optional)
                      </label>
                      <input
                        type="datetime-local"
                        id="visibilityStartDate"
                        value={formData.visibilityStartDate}
                        onChange={(e) => setFormData(prev => ({ ...prev, visibilityStartDate: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-patreon-orange focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-1">Tier will become visible to patrons starting from this date</p>
                    </div>

                    <div>
                      <label htmlFor="visibilityEndDate" className="block text-sm font-medium text-gray-700 mb-1">
                        End Date (optional)
                      </label>
                      <input
                        type="datetime-local"
                        id="visibilityEndDate"
                        value={formData.visibilityEndDate}
                        onChange={(e) => setFormData(prev => ({ ...prev, visibilityEndDate: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-patreon-orange focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-1">Tier will become hidden from patrons after this date</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isPublic"
                  checked={formData.isPublic}
                  onChange={(e) => setFormData(prev => ({ ...prev, isPublic: e.target.checked }))}
                  className="h-4 w-4 border-gray-300 rounded"
                  style={{ color: 'var(--patreon-orange)' } as React.CSSProperties}
                  onFocus={(e) => { e.target.style.boxShadow = '0 0 0 3px rgba(255, 107, 53, 0.1)'; }}
                  onBlur={(e) => { e.target.style.boxShadow = ''; }}
                />
                <label htmlFor="isPublic" className="ml-2 block text-sm text-gray-900">
                  Make this tier publicly visible
                </label>
              </div>
            </div>
          </ModalContent>

          <ModalFooter>
            <Button
              variant="outline"
              onClick={handleCloseModal}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={isSubmitting}
            >
              {editingTier ? 'Update Tier' : 'Create Tier'}
            </Button>
          </ModalFooter>
        </form>
      </Modal>
    </div>
  );
};

export default TiersPage;
